import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useLoadBootstrapData } from 'modules/shift_management/hooks/useFetchEmployees'
import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'

export type IdsType = number[]

export type IdType = number | null | undefined

export type IdsTypes = IdsType | IdType

interface Params {
  effectiveAt: string
  employeeIds: IdsType
  numberOfRestDays: number
}

interface Props extends Params {
  effectiveAt: string
  employeeIds: IdsType
  numberOfRestDays: number
  selectableEmployeesCount: number
  onSuccess: () => void
  contractVersions: ContractVersions
}

const bulkCreatePlanningVersions = ({ effectiveAt, employeeIds, numberOfRestDays }: Params) => {
  return asyncAction(() =>
    PlanningVersionsApi.rpc('bulk_create', {
      employee_ids: employeeIds,
      effective_at: effectiveAt,
      number_of_rest_days_in_cents: numberOfRestDays,
      planning_tool: PLANNING_TOOL.shiftManagement,
    })
  )
}

const fetchPlanningVersions = () => {
  return asyncAction(() => PlanningVersionsApi.fetch())
}

const useImportEmployeesSM = ({
  effectiveAt,
  employeeIds,
  numberOfRestDays,
  selectableEmployeesCount,
  onSuccess,
  contractVersions,
}: Props) => {
  const { loadBootstrapData } = useLoadBootstrapData(contractVersions)

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      track(trackingEvents.shiftManagement.importEmployeesSubmit, {
        number_of_impacted_employees: employeeIds.length,
        effective_date: effectiveAt,
        number_of_available_employees: selectableEmployeesCount,
      })

      const { success } = await bulkCreatePlanningVersions({
        effectiveAt: dateUIToBackend(effectiveAt),
        employeeIds,
        numberOfRestDays,
      })

      if (success) {
        await fetchPlanningVersions()
        /* After create the planning versions in bulk we need to refetch
          only the first page to avoid overload the memory with the information
          of all the employees created (Ex: 5000 imported at the same time) */
        loadBootstrapData()
        onSuccess()
      }
    },
    {
      successFlash: true,
      errorFlash: false,
      successMessage: i18n.t('shift_management.actions.adding_employees_success'),
    }
  )

  return {
    onSubmit: performAction,
    submitting,
  }
}

export default useImportEmployeesSM
